<template>
	<div class="filters-modern" ref="filtersToolbar">
		<div class="filters-toolbar">
			<div
				v-for="filter in activeFilters"
				:key="filter.name"
				:class="[
					'filter-chip',
					{ 'filter-chip--range': filter.type === 'range' || filter.type === 'daterange' },
					{ 'filter-chip--multiselect': filter.type === 'multiselect' },
				]"
			>
				<div class="filter-chip__body">
					<font-awesome-icon :icon="['fas', 'filter']" class="text-muted" />
					<span class="fw-semibold">{{ filter.label }}</span>
					<span class="filter-chip__operator">is</span>

					<template v-if="isTextFilter(filter)">
						<input
							:type="filter.type"
							class="form-control form-control-sm filter-chip__input"
							:placeholder="filter.placeholder"
							v-model="filter.value"
							@input="setFilter"
						/>
					</template>

					<template v-else-if="filter.type === 'select' || filter.type === 'boolean'">
						<select class="form-select form-select-sm filter-chip__select" v-model="filter.value" @change="setFilter">
							<option value="">{{ filter.placeholder || 'Select' }}</option>
							<template v-if="filter.optionValue">
								<option v-for="(option, index) in filter.options" :key="index" :value="option">{{ option }}</option>
							</template>
							<template v-else>
								<option v-for="(option, index) in filter.options" :key="index" :value="option.id">{{ option.name }}</option>
							</template>
						</select>
					</template>

					<template v-else-if="filter.type === 'multiselect'">
						<div class="multi-select">
							<div class="multi-select__tags">
								<span v-for="value in (filter.value || [])" :key="value" class="multi-select__tag">
									{{ getMultiLabel(filter, value) }}
								</span>
								<span v-if="!filter.value || !filter.value.length" class="multi-select__placeholder">Select options</span>
							</div>
							<button type="button" class="btn btn-sm multi-select__toggle" @click.stop="toggleMulti(filter.name)">
								<font-awesome-icon :icon="['fas', 'chevron-down']" />
							</button>
						</div>
						<div v-if="isMultiOpen(filter.name)" class="multi-dropdown" @click.stop>
							<div class="multi-dropdown__search">
								<input
									type="text"
									class="form-control form-control-sm"
									v-model.trim="multiSearch[filter.name]"
									placeholder="Search..."
								/>
							</div>
							<div class="multi-dropdown__list">
								<label v-for="option in filteredMultiOptions(filter)" :key="getMultiValue(filter, option)" class="multi-dropdown__item">
									<input
										type="checkbox"
										:checked="multiIsChecked(filter, option)"
										@change="toggleMultiOption(filter, option)"
									/>
									<span>{{ getOptionLabel(filter, option) }}</span>
								</label>
								<div v-if="!filteredMultiOptions(filter).length" class="multi-dropdown__empty">No results</div>
							</div>
						</div>
					</template>

					<template v-else-if="filter.type === 'range'">
						<div class="filter-chip__range">
							<span class="filter-chip__range-label">From</span>
							<input
								@input="setFilter"
								type="number"
								:min="filter.min"
								:max="filter.max"
								class="form-control form-control-sm filter-chip__input"
								v-model="filter.from"
							/>
						</div>
						<div class="filter-chip__range">
							<span class="filter-chip__range-label">To</span>
							<input
								@input="setFilter"
								type="number"
								:min="filter.min"
								:max="filter.max"
								class="form-control form-control-sm filter-chip__input"
								v-model="filter.to"
							/>
						</div>
					</template>

					<template v-else-if="filter.type === 'daterange'">
						<div class="filter-chip__range">
							<span class="filter-chip__range-label">From</span>
							<input
								@input="setFilter"
								type="date"
								class="form-control form-control-sm filter-chip__input"
								v-model="filter.from"
							/>
						</div>
						<div class="filter-chip__range">
							<span class="filter-chip__range-label">To</span>
							<input
								@input="setFilter"
								type="date"
								class="form-control form-control-sm filter-chip__input"
								v-model="filter.to"
							/>
						</div>
					</template>
				</div>
				<button class="filter-chip__remove" type="button" @click="removeFilter(filter.name)">
					<font-awesome-icon :icon="['fas', 'times']" />
				</button>
			</div>

			<div class="filters-toolbar__controls">
				<button
					class="filter-trigger"
					type="button"
					ref="filtersButton"
					:aria-expanded="filtersMenuOpen"
					:disabled="!availableFilterOptions.length"
					@click="toggleFiltersMenu"
				>
					<font-awesome-icon :icon="['fas', 'filter']" />
					<span>Filter</span>
				</button>
				<div v-if="filtersMenuOpen" class="filters-menu" :style="filtersMenuStyles">
					<div class="filters-menu__search">
						<input
							type="text"
							class="form-control form-control-sm"
							v-model.trim="menuSearch"
							placeholder="Search filters..."
						/>
					</div>
					<div class="filters-menu__list">
						<button
							v-for="option in filteredMenuOptions"
							:key="option.name"
							type="button"
							class="filters-menu__item"
							@click="activateFilter(option.name)"
						>
							{{ option.label }}
						</button>
						<div v-if="!filteredMenuOptions.length" class="filters-menu__empty">No filters found</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '@/api'

export default {
	name: 'LPDataTableFiltersModern',
	props: ['filters'],
	data() {
		return {
			activeFilters: [],
			filtersMenuOpen: false,
			filtersMenuStyles: {},
			menuSearch: '',
			openMulti: null,
			multiSearch: {},
		}
	},
	computed: {
		availableFilterOptions() {
			const activeNames = this.activeFilters.map(filter => filter.name)
			return this.filters.filter(filter => filter.display && !activeNames.includes(filter.name))
		},
		filteredMenuOptions() {
			const term = this.menuSearch.toLowerCase()
			if (!term) {
				return this.availableFilterOptions
			}
			return this.availableFilterOptions.filter(option => (option.label || '').toLowerCase().includes(term))
		},
	},
	created() {
		this.getOptionsDataForFilters()
		this.initActiveFilters()
	},
	mounted() {
		document.addEventListener('click', this.handleClickOutside)
		window.addEventListener('resize', this.refreshFiltersMenuPosition)
		window.addEventListener('scroll', this.refreshFiltersMenuPosition, true)
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleClickOutside)
		window.removeEventListener('resize', this.refreshFiltersMenuPosition)
		window.removeEventListener('scroll', this.refreshFiltersMenuPosition, true)
	},
	watch: {
		filters: {
			handler() {
				this.initActiveFilters()
			},
			deep: true,
		},
	},
	methods: {
		isTextFilter(filter) {
			return ['text', 'number', 'email'].includes(filter.type)
		},
		initActiveFilters() {
			this.activeFilters = this.filters.filter(filter => {
				if (!filter.display) {
					return false
				}
				if (filter.name === 'search') {
					return true
				}
				if (filter.type === 'range' || filter.type === 'daterange') {
					return !!(filter.from || filter.to)
				}
				if (filter.type === 'multiselect') {
					return Array.isArray(filter.value) && filter.value.length > 0
				}
				if (Array.isArray(filter.value)) {
					return filter.value.length > 0
				}
				return !!filter.value
			})
		},
		activateFilter(name) {
			const filter = this.filters.find(item => item.name === name)
			if (!filter) {
				return
			}
			if (!this.activeFilters.some(item => item.name === name)) {
				this.activeFilters.push(filter)
			}
			this.menuSearch = ''
			this.closeFiltersMenu()
		},
		removeFilter(name) {
			const filter = this.filters.find(item => item.name === name)
			this.activeFilters = this.activeFilters.filter(item => item.name !== name)
			if (!filter) {
				return
			}
			if (filter.type === 'range' || filter.type === 'daterange') {
				filter.from = ''
				filter.to = ''
			} else if (filter.type === 'multiselect') {
				filter.value = []
			} else {
				filter.value = ''
			}
			this.setFilter()
		},
		setFilter() {
			this.$emit('getFilters', { filters: this.filters })
		},
		toggleMulti(name) {
			this.openMulti = this.openMulti === name ? null : name
		},
		isMultiOpen(name) {
			return this.openMulti === name
		},
		getOptionValue(filter, option) {
			if (filter.optionValue) {
				return option
			}
			return option?.id ?? option
		},
		getOptionLabel(filter, option) {
			if (filter.optionValue) {
				return option
			}
			return option?.name ?? option
		},
		getMultiValue(filter, option) {
			return this.getOptionValue(filter, option)
		},
		getMultiLabel(filter, value) {
			const options = filter.options || []
			if (filter.optionValue) {
				return value
			}
			const match = options.find(option => this.getOptionValue(filter, option) === value)
			return match ? this.getOptionLabel(filter, match) : value
		},
		filteredMultiOptions(filter) {
			const term = (this.multiSearch[filter.name] || '').toLowerCase()
			const options = filter.options || []
			if (!term) {
				return options
			}
			return options.filter(option => this.getOptionLabel(filter, option).toLowerCase().includes(term))
		},
		multiIsChecked(filter, option) {
			const value = this.getMultiValue(filter, option)
			return Array.isArray(filter.value) && filter.value.includes(value)
		},
		toggleMultiOption(filter, option) {
			const value = this.getMultiValue(filter, option)
			if (!Array.isArray(filter.value)) {
				filter.value = []
			}
			if (filter.value.includes(value)) {
				filter.value = filter.value.filter(item => item !== value)
			} else {
				filter.value.push(value)
			}
			this.setFilter()
		},
		toggleFiltersMenu() {
			if (!this.availableFilterOptions.length) {
				return
			}
			if (this.filtersMenuOpen) {
				this.closeFiltersMenu()
			} else {
				this.updateFiltersMenuPosition()
				this.filtersMenuOpen = true
			}
		},
		closeFiltersMenu() {
			this.filtersMenuOpen = false
			this.menuSearch = ''
		},
		handleClickOutside(event) {
			const toolbar = this.$refs.filtersToolbar
			if (!toolbar) {
				return
			}
			if (!toolbar.contains(event.target)) {
				if (this.filtersMenuOpen) {
					this.closeFiltersMenu()
				}
				this.openMulti = null
			}
		},
		updateFiltersMenuPosition() {
			const button = this.$refs.filtersButton
			if (!button) {
				this.filtersMenuStyles = {}
				return
			}
			const rect = button.getBoundingClientRect()
			const top = rect.bottom + window.scrollY + 8
			const left = rect.left + window.scrollX
			const width = Math.max(rect.width, 320)
			this.filtersMenuStyles = {
				top: `${top}px`,
				left: `${left}px`,
				width: `${width}px`,
			}
		},
		refreshFiltersMenuPosition() {
			if (this.filtersMenuOpen) {
				this.updateFiltersMenuPosition()
			}
		},
		getOptionsDataForFilters() {
			this.filters.forEach(filter => {
				if (filter.optionsEndpoint && filter.optionsEndpoint !== '') {
					restApi.get(filter.optionsEndpoint).then(({ data }) => {
						filter.options = data
					})
				}
			})
		},
	},
}
</script>
<style scoped>
.filters-modern {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin-top: 10px;
}

.multi-select {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	min-height: 32px;
}

.multi-select__tags {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	max-width: 220px;
	overflow: hidden;
}

.multi-select__tag {
	background: #10b981;
	color: #fff;
	border-radius: 999px;
	padding: 0.2rem 0.5rem;
	font-size: 0.75rem;
	font-weight: 600;
}

.multi-select__placeholder {
	color: #6b7280;
	font-size: 0.85rem;
}

.multi-select__toggle {
	border-radius: 999px;
	border: 1px solid #d1d5db;
	padding: 0.15rem 0.5rem;
	background: #fff;
}

.multi-dropdown {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	min-width: 240px;
	max-width: 320px;
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 0.75rem;
	box-shadow: 0 10px 30px rgba(17, 24, 39, 0.15);
	z-index: 1060;
}

.multi-dropdown__search {
	padding: 0.5rem;
	border-bottom: 1px solid #e5e7eb;
}

.multi-dropdown__list {
	max-height: 250px;
	overflow-y: auto;
	padding: 0.35rem 0.5rem 0.5rem 0.5rem;
}

.multi-dropdown__item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.85rem;
	padding: 0.35rem 0.25rem;
	cursor: pointer;
}

.multi-dropdown__item input {
	cursor: pointer;
}

.multi-dropdown__empty {
	padding: 0.5rem 0.25rem;
	color: #6b7280;
	font-size: 0.85rem;
}

.filters-toolbar {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	align-items: center;
}

.filters-toolbar__controls {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	position: relative;
}

.filter-trigger {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.45rem 1.1rem;
	border: 1px solid #dcdfe3;
	border-radius: 999px;
	background: #fff;
	font-weight: 600;
	font-size: 0.95rem;
	color: #4b5563;
	min-height: 44px;
	transition: box-shadow 0.2s ease;
}

.filter-trigger:disabled {
	opacity: 0.6;
	pointer-events: none;
}

.filters-menu {
	position: fixed;
	min-width: 320px;
	max-height: 250px;
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 0.75rem;
	box-shadow: 0 10px 30px rgba(17, 24, 39, 0.15);
	padding: 0.25rem;
	z-index: 1055;
	display: flex;
	flex-direction: column;
}

.filters-menu__search {
	padding: 0.5rem 0.5rem 0.25rem 0.5rem;
}

.filters-menu__list {
	overflow-y: auto;
	padding: 0 0.25rem 0.25rem 0.25rem;
}

.filters-menu__empty {
	padding: 0.5rem 0.75rem;
	color: #6b7280;
	font-size: 0.85rem;
}

.filters-menu__item {
	display: block;
	width: 100%;
	text-align: left;
	padding: 0.4rem 0.75rem;
	border: none;
	background: transparent;
	font-size: 0.9rem;
	border-radius: 0.5rem;
	color: #111827;
}

.filters-menu__item:hover {
	background-color: #f3f4f6;
}

.filter-chip {
	display: inline-flex;
	align-items: stretch;
	border: 1px solid #dcdfe3;
	border-radius: 999px;
	background-color: #fff;
	overflow: hidden;
	min-height: 44px;
}

.filter-chip__body {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.35rem 0.75rem;
	white-space: nowrap;
}

.filter-chip--multiselect {
	overflow: visible;
	position: relative;
}

.filter-chip--multiselect .filter-chip__body {
	overflow: visible;
}

.filter-chip__range {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
}

.filter-chip__range-label {
	text-transform: uppercase;
	font-size: 0.7rem;
	color: #6b7280;
}

.filter-chip__input {
	width: 120px;
	min-width: 0;
	height: 32px;
	padding: 0.2rem 0.5rem;
	font-size: 0.85rem;
}

.filter-chip__select {
	min-width: 160px;
	border-radius: 999px;
}

.filter-chip__remove {
	border: none;
	background: transparent;
	padding: 0.35rem 0.65rem;
	color: #6b7280;
	display: inline-flex;
	align-items: center;
}

.filter-chip__remove:hover {
	color: #b91c1c;
}

.filter-chip__operator {
	text-transform: uppercase;
	font-size: 0.75rem;
	color: #525866;
	background-color: #f1f3f5;
	padding: 0.15rem 0.5rem;
	border-radius: 999px;
}
</style>
